import Color from 'color';
import type { Property } from 'csstype';
import { Range } from 'slate';
import type { NodeEntry, Point } from 'slate';

import type { RenderModelSearchHit } from './paragraph';
import type { AlineText } from '../../slate-api';
import {
  isAIDeleteEdit,
  isAIInsertEdit,
  isDeleteEdit,
  isInsertEdit,
  isTextFormatEdit,
  measureText,
  Queries,
} from '../../slate-api';
import {
  AI_DELETE_HIGHLIGHT_COLOR,
  AI_INSERT_HIGHLIGHT_COLOR,
  getEditColor,
  getPresenceColor,
} from '../../util';
import {
  activeCommentColor,
  activeSearchHitColor,
  activeSelectionColor,
  inactiveCommentColor,
  inactiveSearchHitColor,
  inactiveSelectionColor,
  remoteSelectionOpacity,
} from '../colors';
import type { DrawCaretInstruction } from '../engine/draw-instruction';
import type { RenderContext } from '../engine/render-context';
import type { BaseRenderNodeOptions } from '../engine/rendering-data';
import { CANVAS_Z_INDICES } from '../z-indices';

export interface RenderTextOptions extends BaseRenderNodeOptions {
  baselinePx: number;
  lineHeightPx: number;
  lineMaxDescentPx: number;
  lineMaxFontSizePx: number;
  rMSearchHits: RenderModelSearchHit[];
  spaceWidthPx: 'default' | number;
  /**
   * An additional highlight to render for the text, used for the backgrounds of certain inline
   * elements (such as input fields) and for the selected ¶ mark.
   */
  customHighlight?: {
    color: string;
    debugName?: string;
    height?: 'descent' | 'endOfLine';
  };
  /**
   * If the text node is part of an inline void element, supply these options to override its
   * behavior.
   */
  inlineVoid?: {
    caret: 'start' | 'end' | 'hidden';
    overrideWidthPx?: number;
    showText?: boolean;
  };
  /**
   * Overrides the glyph fill color, winning over the node's own color and any suggested-edit color.
   * Used to render the ¶ mark in the non-printing-character color.
   */
  overrideColor?: string;
  overrideCursor?: { cursor: Property.Cursor; key: string };
  overrideText?: string;
}

const DECORATION_THICKNESS_FACTOR = 0.025;
const DOTTED_UNDERLINE_THICKNESS_FACTOR = 0.0625;

export function renderText(
  renderContext: RenderContext,
  [textNode]: NodeEntry<AlineText> | [AlineText],
  options: RenderTextOptions
) {
  const {
    editor,
    isLastNodeInLine,
    rMDropPoint,
    rMRange: rMTextRange,
    rMRemoteSelIntersections,
    rMSelIntersection,
  } = renderContext;
  const currentUserId = renderContext.withContext('currentUserId');
  const isReadOnly = renderContext.withContext('isReadOnly');
  const suggestedEdits = textNode.suggestedEdits ?? [];
  const {
    baselinePx,
    customHighlight,
    inheritDeleteEdit,
    inheritInsertEdit,
    inlineVoid,
    lineHeightPx,
    lineMaxDescentPx,
    lineMaxFontSizePx,
    overrideColor,
    overrideCursor,
    overrideText,
    rMSearchHits,
    selectionHighlightAbove,
    spaceWidthPx,
  } = options;
  const text = overrideText ?? textNode.text;

  // Suggested edits are stored in reverse chronological order
  const insertEdit = suggestedEdits.find(isInsertEdit) ?? inheritInsertEdit;
  const deleteEdit = suggestedEdits.find(isDeleteEdit) ?? inheritDeleteEdit;
  const formatEdit = suggestedEdits.find(isTextFormatEdit);
  const lastEdit =
    suggestedEdits.find(
      edit => isInsertEdit(edit) || isDeleteEdit(edit) || isTextFormatEdit(edit)
    ) ??
    inheritInsertEdit ??
    inheritDeleteEdit;
  const hasFontColorFormatEdit = suggestedEdits.some(
    edit => isTextFormatEdit(edit) && !!edit.value.fontColor
  );

  const isInsertion = !!insertEdit;
  const isDeletion = !!deleteEdit;
  const isFormatEdit = !!formatEdit;
  const insertColor = isInsertion ? getEditColor(insertEdit, { currentUserId }) : null;
  const deleteColor = isDeletion ? getEditColor(deleteEdit, { currentUserId }) : null;
  const formatEditColor = isFormatEdit ? getEditColor(formatEdit, { currentUserId }) : null;

  const currentUserAIEdit = suggestedEdits.find(
    edit => (isAIInsertEdit(edit) || isAIDeleteEdit(edit)) && edit.authorId === currentUserId
  );

  const font = Queries.getLeafFontStyle(textNode);
  const { fontSizePx } = font;
  const baseTextColor = Queries.renderedFontColor(textNode);
  const highlightColor = Queries.renderedHighlightColor(textNode);
  const verticalAlignOffset = Queries.renderedVerticalAlignOffset(textNode);
  const isStruckthrough = isDeletion || Queries.isStrikethroughActive(textNode);
  const baseIsUnderlined = Queries.isUnderlineActive(textNode);
  const isUnderlined = isInsertion || isFormatEdit || baseIsUnderlined;

  /**
   * The color we use to render the text. If the text has one or more suggested edits, we use the
   * color of the most recent edit. The exception is if one or more edits affect the font color, in
   * which case we avoid overriding that color.
   */
  const renderedTextColor =
    overrideColor ??
    (!hasFontColorFormatEdit && lastEdit
      ? getEditColor(lastEdit, { currentUserId })
      : baseTextColor);

  const rMSearchHitIntersections: RenderModelSearchHit[] = rMTextRange
    ? rMSearchHits.flatMap(rMRange => {
        const intersection = Range.intersection(rMTextRange, rMRange);
        if (!intersection) return [];
        const { anchor, focus } = intersection;
        return { ...rMRange, anchor, focus };
      })
    : [];

  // The top and descent will differ from those stored on rMText for subscript and superscript text
  const { metrics, textBaselineToTopWithPaddingPx, textDescentPx } =
    Queries.textFontMetrics(textNode);

  let cachedWidthPx: number | null = null;
  function getTextWidthPx(options: { end?: number } = {}): number {
    const { end = text.length } = options;
    if (inlineVoid?.overrideWidthPx) return inlineVoid.overrideWidthPx;
    if (end === text.length && cachedWidthPx !== null) return cachedWidthPx;
    const widthPx = Queries.getTextWidthPx(editor, {
      isLastNodeInLine: isLastNodeInLine ?? false,
      offset: { end, start: 0 },
      rMLine: { spaceWidthPx },
      textNode: { ...textNode, text },
    });
    if (end === text.length) cachedWidthPx = widthPx;
    return widthPx;
  }

  function renderHighlight(
    color: string | Color,
    options: {
      debugName?: string;
      height?: 'descent' | 'endOfLine';
      range?: Range;
    } = {}
  ) {
    const { debugName, height = 'descent', range } = options;
    let distanceToStart: number;
    let distanceToEnd: number;

    if (
      range &&
      // If we're rendering text in an inline void, ignore the range and always highlight everything
      !inlineVoid
    ) {
      if (!Range.isExpanded(range)) return;
      const [{ offset: startOffset }, { offset: endOffset }] = Range.edges(range);
      distanceToStart = getTextWidthPx({ end: startOffset });
      distanceToEnd = getTextWidthPx({ end: endOffset });
    } else {
      distanceToStart = 0;
      distanceToEnd = getTextWidthPx();
    }

    const highlightWidth = distanceToEnd - distanceToStart;
    const highlightHeight: number = {
      descent: baselinePx + lineMaxDescentPx,
      endOfLine: lineHeightPx,
    }[height];

    renderContext.drawRect(distanceToStart, 0, highlightWidth, highlightHeight, {
      debugName,
      fillColor: color.toString(),
      zIndex: CANVAS_Z_INDICES.TEXT_HIGHLIGHT,
    });
  }

  function renderCaret(
    caretType: DrawCaretInstruction['caretType'],
    point: Point,
    options: { color?: string | Color; label?: string } = {}
  ) {
    const { color, label } = options;

    let distanceToPoint;

    switch (inlineVoid?.caret) {
      case 'start':
        distanceToPoint = 0;
        break;

      case 'end':
        distanceToPoint = getTextWidthPx();
        break;

      case 'hidden':
        return;

      case undefined:
        distanceToPoint = getTextWidthPx({ end: point.offset });
        break;
    }

    renderContext.drawCaret(
      distanceToPoint,
      baselinePx - textBaselineToTopWithPaddingPx + verticalAlignOffset,
      {
        caretType,
        color: color?.toString(),
        height: textBaselineToTopWithPaddingPx + textDescentPx,
        label,
      }
    );
  }

  // Custom highlight
  if (customHighlight)
    renderHighlight(customHighlight.color, {
      debugName: customHighlight.debugName,
      height: customHighlight.height,
    });

  // Text highlight
  if (highlightColor) renderHighlight(highlightColor);

  // AI edits
  if (currentUserAIEdit) {
    const aiHighlightColor = isAIInsertEdit(currentUserAIEdit)
      ? AI_INSERT_HIGHLIGHT_COLOR
      : AI_DELETE_HIGHLIGHT_COLOR;
    renderHighlight(aiHighlightColor);
  }

  // Unresolved comments
  const commentIds = Queries.renderedCommentIds(textNode);
  const unresolvedCommentIds = commentIds.filter(commentId => {
    const comment = renderContext.withContextSelector('commentsById', comments =>
      comments.get(commentId)
    );
    return !!comment && !comment.resolved;
  });

  const activeCommentId = unresolvedCommentIds.length
    ? renderContext.withContext('activeCommentId')
    : null;

  for (const commentId of unresolvedCommentIds) {
    const isActive = commentId === activeCommentId;
    const highlightColor = isActive ? activeCommentColor : inactiveCommentColor;

    renderHighlight(highlightColor, { debugName: 'commentHighlight' });
  }

  if (unresolvedCommentIds.length)
    renderContext.drawAnnotationMarker({ commentIds: unresolvedCommentIds });

  // Search hits
  for (const rMSearchHit of rMSearchHitIntersections)
    renderHighlight(
      rMSearchHit.isActive ? activeSearchHitColor.toString() : inactiveSearchHitColor.toString(),
      { range: rMSearchHit }
    );

  // Remote selections
  for (const remoteSelection of Object.values(rMRemoteSelIntersections ?? {})) {
    const {
      displayName,
      focus,
      gridCellHighlight,
      id,
      isExpanded,
      isGridSelection,
      isOriginalFocus,
    } = remoteSelection;
    if (!id) continue;
    const color = getPresenceColor(id, { currentUserId });
    const highlightColor = new Color(color).alpha(remoteSelectionOpacity);
    /**
     * A grid selection isn't a contiguous range, so its own entry never draws a highlight; instead
     * it's expanded into one `gridCellHighlight` entry per selected cell (see Document.tsx), and the
     * original entry only carries the caret/name.
     */
    if (isExpanded && (gridCellHighlight || !isGridSelection))
      renderHighlight(highlightColor, { height: 'endOfLine', range: remoteSelection });
    if (isOriginalFocus && !gridCellHighlight)
      renderCaret('remote', focus, { color, label: displayName });
  }

  // Local selection
  const isSelectionVisible =
    !!rMSelIntersection &&
    !selectionHighlightAbove &&
    renderContext.withEditor(editor => editor.selection && !editor.selection.listMarker);

  const isSelectionCollapsed = isSelectionVisible && !rMSelIntersection.isExpanded;

  if (isSelectionVisible) {
    if (isSelectionCollapsed) {
      if (!isReadOnly) renderCaret('insertion', rMSelIntersection.focus);
    } else {
      const isFocused = renderContext.withContext('isFocused');
      renderHighlight(isFocused ? activeSelectionColor : inactiveSelectionColor, {
        height: 'endOfLine',
        range: rMSelIntersection,
      });
    }
  }

  // Drop caret
  if (rMDropPoint) renderCaret('drop', rMDropPoint);

  // Text
  if (!inlineVoid || inlineVoid.showText) {
    const drawTextOptions = { color: renderedTextColor, font };
    const renderedBaseline = baselinePx + verticalAlignOffset;

    if (spaceWidthPx === 'default') {
      renderContext.drawText(0, renderedBaseline, text, drawTextOptions);
    } else {
      const wordsAndSpaces = Queries.getWordsAndSpaces(text);
      let x = 0;
      for (const wordOrSpace of wordsAndSpaces) {
        renderContext.drawText(x, renderedBaseline, wordOrSpace, drawTextOptions);
        x +=
          wordOrSpace === ' '
            ? spaceWidthPx
            : measureText(editor, { text: wordOrSpace, textNode: textNode });
      }
    }
  }

  // Strikethrough
  if (isStruckthrough)
    renderContext.drawHorizontalLine({
      color: deleteColor ?? baseTextColor,
      debugName: 'strikethrough',
      endX: getTextWidthPx(),
      startX: 0,
      thickness: fontSizePx * DECORATION_THICKNESS_FACTOR,
      y: baselinePx + metrics.middle * fontSizePx + verticalAlignOffset,
    });

  // Underline
  if (isUnderlined) {
    const isFormatUnderline = !isInsertion && isFormatEdit && !baseIsUnderlined;
    const underlineThickness = isFormatUnderline
      ? fontSizePx * DOTTED_UNDERLINE_THICKNESS_FACTOR
      : lineMaxFontSizePx * DECORATION_THICKNESS_FACTOR;

    renderContext.drawHorizontalLine({
      color: insertColor ?? formatEditColor ?? baseTextColor,
      /** Show a dotted underline for format edits. */
      dashPattern: isFormatUnderline ? [0, underlineThickness * 2] : undefined,
      debugName: 'underline',
      endX: getTextWidthPx(),
      lineCap: isFormatUnderline ? 'round' : undefined,
      startX: 0,
      thickness: underlineThickness,
      y: baselinePx + lineMaxDescentPx / 2,
    });
  }

  // Mouse cursor
  if (overrideCursor)
    renderContext.setMouseCursor(
      0,
      0,
      getTextWidthPx(),
      baselinePx + lineMaxDescentPx,
      overrideCursor
    );
}
